/**
 * SeasonalFood widget -- Seasonal food, health tips, and micro-workout suggestions.
 */

import type { Widget, WidgetContext } from '../types';

interface SeasonalTip {
  foods: string[];
  healthTip: string;
  workout: string;
}

/** Season aware of the user's hemisphere. Northern (lat ≥ 0) gets the
 *  conventional Mar-May = spring etc. Southern hemisphere flips the cycle
 *  so a Sydney user in May sees "autumn" not "spring" — important for
 *  food / workout suggestions to match the user's actual climate. */
function getSeason(lat: number | null): 'winter' | 'spring' | 'summer' | 'autumn' {
  const month = new Date().getMonth();
  const north = lat === null || lat >= 0;
  if (north) {
    if (month >= 2 && month <= 4) return 'spring';
    if (month >= 5 && month <= 7) return 'summer';
    if (month >= 8 && month <= 10) return 'autumn';
    return 'winter';
  }
  // Southern hemisphere — seasons inverted by 6 months
  if (month >= 2 && month <= 4) return 'autumn';
  if (month >= 5 && month <= 7) return 'winter';
  if (month >= 8 && month <= 10) return 'spring';
  return 'summer';
}

function getSeasonalData(
  t: (k: string, p?: Record<string, string | number>) => string,
  temp: number | null,
  lat: number | null,
): SeasonalTip {
  const season = getSeason(lat);
  const day = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);

  const foods: Record<string, string[]> = {
    winter: [t('food_citrus'), t('food_root_veg'), t('food_soups'), t('food_nuts')],
    spring: [t('food_strawberries'), t('food_asparagus'), t('food_peas'), t('food_spinach')],
    summer: [t('food_watermelon'), t('food_tomatoes'), t('food_salads'), t('food_cucumbers')],
    autumn: [t('food_pumpkin'), t('food_apples'), t('food_mushrooms'), t('food_grapes')],
  };

  const workouts = [
    t('workout_stretching'),
    t('workout_squats'),
    t('workout_walking'),
    t('workout_breathing'),
    t('workout_plank'),
    t('workout_yoga'),
    t('workout_jumping_jacks'),
  ];

  let healthTip = '';
  if (temp !== null) {
    if (temp < 5) healthTip = t('health_tip_blankets');
    else if (temp < 10) healthTip = t('health_tip_cold_tea');
    else if (temp > 32) healthTip = t('health_tip_hydrate');
    else if (temp > 28) healthTip = t('health_tip_avoid_midday');
    else if (season === 'winter') healthTip = t('health_tip_vitamin_d');
    else healthTip = t('health_tip_general');
  } else {
    healthTip = season === 'winter' ? t('health_tip_vitamin_d') : t('health_tip_general');
  }

  // Adapt workout to weather
  let workout = workouts[day % workouts.length];
  if (temp !== null && temp < 5) {
    workout = t('workout_indoor_prefix') + ' ' + workout;
  } else if (temp !== null && temp > 20 && temp < 30) {
    workout = t('workout_outdoor_prefix') + ' ' + workout;
  }

  return {
    foods: foods[season],
    healthTip,
    workout,
  };
}

export const SeasonalFood: Widget = {
  id: 'seasonal-food',
  label: { en: 'Seasonal Tips', fr: 'Conseils saisonniers', ar: 'نصائح موسمية', es: 'Consejos estacionales', de: 'Saisonale Tipps' },
  icon: '🥗',
  defaultVisible: true,
  defaultOrder: 13,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const weather = ctx.getWeatherData();
    const temp = weather?.temperature ?? null;
    const loc = ctx.getLocationService();
    const data = getSeasonalData(ctx.t, temp, loc.lat);
    const season = getSeason(loc.lat);
    const seasonLabel = ctx.t(`season_${season}`);

    if (size === 'compact') {
      container.innerHTML = `
        <div class="rounded-2xl p-4" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span>🥗</span>
              <span class="text-sm" style="color:var(--text-primary);">${data.foods[0]} · ${data.foods[1]}</span>
            </div>
            <span class="text-xs" style="color:var(--accent);">💡 ${data.healthTip.substring(0, 40)}${data.healthTip.length > 40 ? '...' : ''}</span>
          </div>
        </div>`;
      return;
    }

    container.innerHTML = `
      <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
        <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">🥗 ${ctx.t('widget_seasonal_title')} · ${seasonLabel}</h3>

        <div class="mb-3">
          <p class="text-xs font-medium mb-2" style="color:var(--text-secondary);">${ctx.t('widget_seasonal_foods')}</p>
          <div class="flex flex-wrap gap-2">
            ${data.foods.map((f) => `
              <span class="text-xs px-2 py-1 rounded-full" style="background:#0A0A0A15;color:#0A0A0A;">${f}</span>
            `).join('')}
          </div>
        </div>

        <div class="mb-3 p-3 rounded-xl" style="background:#F59E0B08;border:1px solid #F59E0B15;">
          <p class="text-xs font-medium" style="color:#F59E0B;">💡 ${data.healthTip}</p>
        </div>

        <div class="p-3 rounded-xl" style="background:#3B82F608;border:1px solid #3B82F615;">
          <p class="text-xs font-medium mb-1" style="color:#3B82F6;">💪 ${ctx.t('widget_micro_workout')}</p>
          <p class="text-xs" style="color:var(--text-primary);">${data.workout}</p>
        </div>
      </div>`;
  },
};
